"use client"

import type React from "react"

import { useState, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Progress } from "@/components/ui/progress"
import { Slider } from "@/components/ui/slider"
import { Target, Edit, Save, TrendingUp, BarChart3 } from "lucide-react"

interface Campaign {
  url: string
  traffic: number
  conversions: number
  bounceRate: number
}

interface CampaignStats {
  abTestEnabled: boolean
  campaignA: Campaign
  campaignB: Campaign
}

interface CampaignControlContentProps {
  campaignStats: CampaignStats
  setCampaignStats: React.Dispatch<React.SetStateAction<CampaignStats>>
}

export function CampaignControlContent({ campaignStats, setCampaignStats }: CampaignControlContentProps) {
  const [editingCampaign, setEditingCampaign] = useState<string | null>(null)
  const [trafficSplit, setTrafficSplit] = useState([50])

  // Safe campaign stats with defaults
  const safeCampaignStats: CampaignStats = {
    abTestEnabled: campaignStats?.abTestEnabled ?? false,
    campaignA: {
      url: campaignStats?.campaignA?.url ?? "https://example.com/campaign-a",
      traffic: campaignStats?.campaignA?.traffic ?? 50,
      conversions: campaignStats?.campaignA?.conversions ?? 0,
      bounceRate: campaignStats?.campaignA?.bounceRate ?? 0,
    },
    campaignB: {
      url: campaignStats?.campaignB?.url ?? "https://example.com/campaign-b",
      traffic: campaignStats?.campaignB?.traffic ?? 50,
      conversions: campaignStats?.campaignB?.conversions ?? 0,
      bounceRate: campaignStats?.campaignB?.bounceRate ?? 0,
    },
  }

  // Safe URL update with validation
  const updateCampaignUrl = useCallback(
    (campaign: "A" | "B", newUrl: string) => {
      try {
        if (typeof setCampaignStats !== "function") return

        // Basic URL validation
        const sanitizedUrl = newUrl.trim()

        setCampaignStats((prev: CampaignStats) => ({
          ...prev,
          [`campaign${campaign}`]: {
            ...prev[`campaign${campaign}`],
            url: sanitizedUrl,
          },
        }))
      } catch (error) {
        console.error("Error updating campaign URL:", error)
      }
    },
    [setCampaignStats],
  )

  // Safe A/B test toggle
  const toggleABTest = useCallback(() => {
    try {
      if (typeof setCampaignStats !== "function") return

      setCampaignStats((prev: CampaignStats) => ({
        ...prev,
        abTestEnabled: !prev.abTestEnabled,
      }))
    } catch (error) {
      console.error("Error toggling A/B test:", error)
    }
  }, [setCampaignStats])

  // Safe editing state management
  const handleEditClick = useCallback(
    (campaign: string) => {
      try {
        setEditingCampaign(editingCampaign === campaign ? null : campaign)
      } catch (error) {
        console.error("Error handling edit click:", error)
      }
    },
    [editingCampaign],
  )

  const handleSaveClick = useCallback(() => {
    try {
      setEditingCampaign(null)
    } catch (error) {
      console.error("Error handling save click:", error)
    }
  }, [])

  // Safe traffic split update
  const handleTrafficSplitChange = useCallback((value: number[]) => {
    try {
      const splitValue = Math.max(0, Math.min(100, value[0] || 50))
      setTrafficSplit([splitValue])
    } catch (error) {
      console.error("Error updating traffic split:", error)
    }
  }, [])

  // Safe percentage calculation
  const calculateConversionRate = useCallback((campaign: Campaign, totalConversions: number): string => {
    try {
      if (totalConversions === 0) return "0.0"
      const rate = (campaign.conversions / totalConversions) * 100
      return isNaN(rate) ? "0.0" : rate.toFixed(1)
    } catch (error) {
      console.error("Error calculating conversion rate:", error)
      return "0.0"
    }
  }, [])

  // Safe number formatting
  const formatNumber = useCallback((num: number): string => {
    try {
      if (typeof num !== "number" || isNaN(num)) return "0"
      return num.toString()
    } catch (error) {
      return "0"
    }
  }, [])

  // Calculate totals safely
  const totalConversions = safeCampaignStats.campaignA.conversions + safeCampaignStats.campaignB.conversions
  const winningCampaign = safeCampaignStats.campaignA.conversions > safeCampaignStats.campaignB.conversions ? "A" : "B"

  return (
    <>
      {/* Campaign Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-slate-900/50 border-blue-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Target className="h-6 w-6 text-blue-500" />
              <Badge
                className={
                  safeCampaignStats.abTestEnabled
                    ? "bg-green-500/20 text-green-400 border-green-500/50"
                    : "bg-red-500/20 text-red-400 border-red-500/50"
                }
              >
                {safeCampaignStats.abTestEnabled ? "Active" : "Inactive"}
              </Badge>
            </div>
            <div className="text-2xl font-bold text-blue-400">A/B Testing</div>
            <div className="text-sm text-slate-400">Campaign Split Testing</div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-green-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-6 w-6 text-green-500" />
              <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Total</Badge>
            </div>
            <div className="text-2xl font-bold text-green-400">{formatNumber(totalConversions)}</div>
            <div className="text-sm text-slate-400">Total Conversions</div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-purple-500/30 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <BarChart3 className="h-6 w-6 text-purple-500" />
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50">Winner</Badge>
            </div>
            <div className="text-2xl font-bold text-purple-400">Campaign {winningCampaign}</div>
            <div className="text-sm text-slate-400">Best Performer</div>
          </CardContent>
        </Card>
      </div>

      {/* A/B Testing Control */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm mb-6">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center">
            <Target className="mr-2 h-5 w-5 text-purple-500" />
            A/B Testing Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm text-slate-300">Enable A/B Testing</Label>
                  <div className="text-xs text-slate-500">Split traffic between campaigns</div>
                </div>
                <Switch checked={safeCampaignStats.abTestEnabled} onCheckedChange={toggleABTest} />
              </div>

              <div>
                <Label className="text-sm text-slate-300 mb-2 block">Traffic Split Ratio</Label>
                <div className="flex items-center space-x-3">
                  <span className="text-blue-400 font-mono w-8">A</span>
                  <Slider
                    value={trafficSplit}
                    onValueChange={handleTrafficSplitChange}
                    max={100}
                    step={5}
                    className="flex-1"
                    disabled={!safeCampaignStats.abTestEnabled}
                  />
                  <span className="text-purple-400 font-mono w-8">B</span>
                </div>
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>{trafficSplit[0]}%</span>
                  <span>{100 - trafficSplit[0]}%</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-slate-200 mb-3">Performance Comparison</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Campaign A Conversion Rate</span>
                  <span className="text-blue-400 font-bold">
                    {calculateConversionRate(safeCampaignStats.campaignA, totalConversions)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Campaign B Conversion Rate</span>
                  <span className="text-purple-400 font-bold">
                    {calculateConversionRate(safeCampaignStats.campaignB, totalConversions)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Statistical Significance</span>
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/50">95%</Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Campaign Management */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Campaign A */}
        <Card className="bg-slate-900/50 border-blue-500/30 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-slate-100 flex items-center">
                <div className="w-4 h-4 rounded-full bg-blue-500 mr-2"></div>
                Campaign A
              </CardTitle>
              <Button size="sm" variant="ghost" onClick={() => handleEditClick("A")}>
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-slate-300 mb-2 block">Campaign URL</Label>
                {editingCampaign === "A" ? (
                  <div className="flex space-x-2">
                    <Input
                      value={safeCampaignStats.campaignA.url}
                      onChange={(e) => updateCampaignUrl("A", e.target.value)}
                      className="bg-slate-800/50 border-slate-700"
                    />
                    <Button size="sm" onClick={handleSaveClick}>
                      <Save className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-sm font-mono text-slate-300 bg-slate-800/50 rounded px-3 py-2 border border-slate-700/50 truncate">
                    {safeCampaignStats.campaignA.url}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                  <div className="text-xs text-slate-500 mb-1">Traffic</div>
                  <div className="text-lg font-bold text-blue-400">
                    {formatNumber(safeCampaignStats.campaignA.traffic)}%
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                  <div className="text-xs text-slate-500 mb-1">Conversions</div>
                  <div className="text-lg font-bold text-green-400">
                    {formatNumber(safeCampaignStats.campaignA.conversions)}
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                  <div className="text-xs text-slate-500 mb-1">Bounce Rate</div>
                  <div className="text-lg font-bold text-red-400">
                    {formatNumber(safeCampaignStats.campaignA.bounceRate)}%
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Performance</span>
                  <span className="text-sm text-blue-400">{formatNumber(safeCampaignStats.campaignA.traffic)}%</span>
                </div>
                <Progress value={safeCampaignStats.campaignA.traffic} className="h-2">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full"
                    style={{ width: `${Math.max(0, Math.min(100, safeCampaignStats.campaignA.traffic))}%` }}
                  />
                </Progress>
              </div>

              <div className="flex space-x-2">
                <Button size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700">
                  Test Campaign
                </Button>
                <Button size="sm" variant="outline" className="flex-1 bg-transparent">
                  View Analytics
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Campaign B */}
        <Card className="bg-slate-900/50 border-purple-500/30 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-slate-100 flex items-center">
                <div className="w-4 h-4 rounded-full bg-purple-500 mr-2"></div>
                Campaign B
              </CardTitle>
              <Button size="sm" variant="ghost" onClick={() => handleEditClick("B")}>
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-slate-300 mb-2 block">Campaign URL</Label>
                {editingCampaign === "B" ? (
                  <div className="flex space-x-2">
                    <Input
                      value={safeCampaignStats.campaignB.url}
                      onChange={(e) => updateCampaignUrl("B", e.target.value)}
                      className="bg-slate-800/50 border-slate-700"
                    />
                    <Button size="sm" onClick={handleSaveClick}>
                      <Save className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-sm font-mono text-slate-300 bg-slate-800/50 rounded px-3 py-2 border border-slate-700/50 truncate">
                    {safeCampaignStats.campaignB.url}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                  <div className="text-xs text-slate-500 mb-1">Traffic</div>
                  <div className="text-lg font-bold text-purple-400">
                    {formatNumber(safeCampaignStats.campaignB.traffic)}%
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                  <div className="text-xs text-slate-500 mb-1">Conversions</div>
                  <div className="text-lg font-bold text-green-400">
                    {formatNumber(safeCampaignStats.campaignB.conversions)}
                  </div>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                  <div className="text-xs text-slate-500 mb-1">Bounce Rate</div>
                  <div className="text-lg font-bold text-red-400">
                    {formatNumber(safeCampaignStats.campaignB.bounceRate)}%
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Performance</span>
                  <span className="text-sm text-purple-400">{formatNumber(safeCampaignStats.campaignB.traffic)}%</span>
                </div>
                <Progress value={safeCampaignStats.campaignB.traffic} className="h-2">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full"
                    style={{ width: `${Math.max(0, Math.min(100, safeCampaignStats.campaignB.traffic))}%` }}
                  />
                </Progress>
              </div>

              <div className="flex space-x-2">
                <Button size="sm" className="flex-1 bg-purple-600 hover:bg-purple-700">
                  Test Campaign
                </Button>
                <Button size="sm" variant="outline" className="flex-1 bg-transparent">
                  View Analytics
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
